import { useMutation, useQueryClient } from "@tanstack/react-query";

import { Modal } from "../../components/Modal/Modal";
import { Button } from "../../components/Button/Button";
import { Alert } from "../../components/Alert/Alert";
import { useToast } from "../../components/Toast/Toast";
import { ApiError, describeError } from "../../lib/errors";

import { AdminUser, disableUser } from "./api";
import { UserPreviewCard } from "./UserPreviewCard";

interface Props {
  open: boolean;
  user: AdminUser | null;
  onClose: () => void;
}

/**
 * Disable-account confirmation modal. Mockup L621-L647.
 *
 * Danger style with a session-revocation warning. Project memberships are
 * preserved — re-enable restores access.
 */
export function DisableModal({ open, user, onClose }: Props) {
  const qc = useQueryClient();
  const { push } = useToast();

  const mut = useMutation({
    mutationFn: (id: string) => disableUser(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["users"] });
      push({
        variant: "success",
        message: "User disabled.",
      });
      onClose();
    },
    onError: (err: unknown) => {
      push({
        variant: "danger",
        message:
          err instanceof ApiError
            ? describeError(err.code)
            : describeError("internal"),
      });
    },
  });

  if (!user) return null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Disable ${user.name}?`}
      footer={
        <>
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={mut.isPending}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={() => mut.mutate(user.id)}
            disabled={mut.isPending}
            loading={mut.isPending}
          >
            Disable account
          </Button>
        </>
      }
    >
      <UserPreviewCard user={user} />
      <Alert
        variant="danger"
        title="All sessions will be revoked immediately"
      >
        They'll be signed out everywhere and won't be able to sign in. Their
        project memberships are kept — you can re-enable later.
      </Alert>
    </Modal>
  );
}
